<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { cortexProcessingRunsAPI } from 'dashboard/api/cortex';
import NextButton from 'dashboard/components-next/button/Button.vue';
import CortexCurationQueue from './CortexCurationQueue.vue';
import CortexHistoricalProcessing from './CortexHistoricalProcessing.vue';
import CortexSectionGuide from './CortexSectionGuide.vue';

const { t } = useI18n();
const activeSection = ref('new');
const checkingAccess = ref(true);
const accessError = ref('');
const resultContext = ref(null);

const checkAccess = async () => {
  checkingAccess.value = true;
  accessError.value = '';
  try {
    await cortexProcessingRunsAPI.list({ per_page: 1 });
  } catch (error) {
    accessError.value = error?.response?.data?.message || t('CORTEX.API.ERROR');
  } finally {
    checkingAccess.value = false;
  }
};

onMounted(checkAccess);

const sections = computed(() => [
  {
    key: 'new',
    icon: 'i-lucide-search-check',
    label: t('CORTEX.PROCESSING.WORKSPACE.NEW_TITLE'),
    description: t('CORTEX.PROCESSING.WORKSPACE.NEW_DESCRIPTION'),
  },
  {
    key: 'history',
    icon: 'i-lucide-history',
    label: t('CORTEX.PROCESSING.V6.HISTORY.TITLE'),
    description: t('CORTEX.PROCESSING.V6.HISTORY.DESCRIPTION'),
  },
  {
    key: 'curation',
    icon: 'i-lucide-list-checks',
    label: t('CORTEX.PROCESSING.WORKSPACE.CURATION_TITLE'),
    description: t('CORTEX.PROCESSING.WORKSPACE.CURATION_DESCRIPTION'),
  },
]);

const selectSection = section => {
  resultContext.value = null;
  activeSection.value = section.key;
};
const showResults = context => {
  resultContext.value = context;
  activeSection.value = 'curation';
};
</script>

<template>
  <CortexSectionGuide
    :title="t('CORTEX.PROCESSING.V6.TITLE')"
    :description="t('CORTEX.PROCESSING.V6.DESCRIPTION')"
    :sections="sections"
    :active-section="activeSection"
    @section-changed="selectSection"
  >
    <div
      v-if="checkingAccess"
      role="status"
      class="flex items-center gap-2 text-sm text-n-slate-11"
    >
      <span
        class="i-lucide-loader-2 size-4 animate-spin motion-reduce:animate-none"
        aria-hidden="true"
      />
      {{ $t('CORTEX.PROCESSING.V6.DETAILS.LOADING') }}
    </div>
    <div
      v-else-if="accessError"
      data-testid="cortex-processing-access-blocked"
      role="alert"
      class="grid gap-3"
    >
      <p class="text-sm text-n-slate-12">{{ accessError }}</p>
      <NextButton
        type="button"
        outline
        icon="i-lucide-refresh-cw"
        class="justify-self-start"
        :label="$t('CORTEX.PROCESSING.V6.REFRESH')"
        @click="checkAccess"
      />
    </div>
    <template v-else>
      <CortexHistoricalProcessing
        v-if="['new', 'history'].includes(activeSection)"
        :active-section="activeSection"
        data-testid="cortex-historical-processing"
        @view-results="showResults"
      />
      <CortexCurationQueue
        v-else
        :key="resultContext?.assessmentId || 'all'"
        :assessment-id="resultContext?.assessmentId"
        :conversation-display-id="resultContext?.conversationDisplayId"
        data-testid="cortex-processing-curation"
      />
    </template>
  </CortexSectionGuide>
</template>
